import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { forkJoin, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { DOCUMENTATION_CATALOG } from '../data/constants/documentation-catalog.constant'
import { DecisionProgress, DecisionState, DocumentationDecision, DocumentationEntry, DocumentationThread } from '../data/interfaces/documentation.interface'
import { DocumentationService } from '../data/services/api/documentation.service'
import { DECISION_STATE_LABEL, decisionProgress, decisionState, extractDecisions } from '../data/utils/decisions.util'

/** Una fila de la tabla: la decisión con su documento y en qué punto está. */
interface DecisionRow {
  doc: DocumentationEntry
  decision: DocumentationDecision
  state: DecisionState
  label: string
  /** Quién dejó la última respuesta y cuándo — vacío si nadie ha contestado. */
  who: string
  when: string
}

/**
 * La foto global de la revisión: las 24 decisiones de los tres manuales en una sola pantalla.
 *
 * Carga los assets de los manuales para derivar las decisiones. Es caro comparado con leer un
 * contador, pero es una navegación explícita —no un costo de fondo— y evita la alternativa mala:
 * declarar la lista de decisiones en el catálogo, que sería una segunda fuente de verdad
 * desfasándose en silencio cada vez que alguien edite un manual.
 */
@Component({
  selector: 'app-documentation-progress',
  templateUrl: './documentation-progress.component.html',
  styleUrls: ['./documentation-progress.component.scss'],
})
export class DocumentationProgressComponent implements OnInit {
  rows: DecisionRow[] = []
  progress: DecisionProgress = { total: 0, answered: 0, closed: 0 }
  porDocumento: Array<{ doc: DocumentationEntry; progress: DecisionProgress }> = []
  loading = true
  loadError = ''
  filtro: DecisionState | 'all' = 'all'

  constructor(private readonly documentationService: DocumentationService, private readonly router: Router) {}

  ngOnInit(): void {
    //* Solo los manuales: las guías de plataforma no plantean decisiones.
    const manuales = DOCUMENTATION_CATALOG.filter((doc) => doc.available && doc.kind === 'manual')

    forkJoin(
      manuales.map((doc) =>
        forkJoin({
          html: this.documentationService.getContent(doc.asset),
          threads: this.documentationService.getThreads(doc.slug).pipe(catchError(() => of(null))),
        }).pipe(map((res) => ({ doc, ...res })))
      )
    ).subscribe({
      next: (resultados) => {
        this.loading = false
        this.build(resultados)
      },
      error: () => {
        this.loading = false
        this.loadError = 'No se pudo cargar el avance de la revisión.'
      },
    })
  }

  private build(resultados: Array<{ doc: DocumentationEntry; html: string; threads: any }>): void {
    const rows: DecisionRow[] = []
    this.porDocumento = []

    for (const { doc, html, threads: res } of resultados) {
      const decisions = extractDecisions(html ?? '')
      const threads: DocumentationThread[] = Array.isArray(res?.data) ? res.data : []

      this.porDocumento.push({ doc, progress: decisionProgress(decisions, threads) })

      for (const decision of decisions) {
        const state = decisionState(threads, decision.blockId)
        const ultimo = this.ultimaRespuesta(threads, decision.blockId)

        rows.push({
          doc,
          decision,
          state,
          label: DECISION_STATE_LABEL[state],
          who: ultimo ? `${ultimo.author.names} ${ultimo.author.lastNames}`.trim() : '',
          when: ultimo?.createdAt ?? '',
        })
      }
    }

    this.rows = rows
    this.progress = rows.reduce(
      (acc, row) => {
        if (row.state !== 'pending') acc.answered++
        if (row.state === 'closed') acc.closed++
        return acc
      },
      { total: rows.length, answered: 0, closed: 0 }
    )
  }

  /** El comentario más reciente de esa decisión, sea del hilo que sea. */
  private ultimaRespuesta(threads: DocumentationThread[], blockId: string) {
    return threads
      .filter((thread) => thread.blockId === blockId)
      .flatMap((thread) => thread.comments ?? [])
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  }

  get visibles(): DecisionRow[] {
    return this.filtro === 'all' ? this.rows : this.rows.filter((row) => row.state === this.filtro)
  }

  /**
   * La tabla va agrupada por manual y no con una columna «Manual»: repetir el mismo nombre en
   * diez renglones seguidos era ruido, y el avance de cada manual vive en su propio encabezado
   * en vez de en un bloque aparte que decía lo mismo dos veces.
   */
  get grupos(): Array<{ doc: DocumentationEntry; progress: DecisionProgress; rows: DecisionRow[] }> {
    const visibles = this.visibles
    return this.porDocumento
      .map(({ doc, progress }) => ({ doc, progress, rows: visibles.filter((row) => row.doc.slug === doc.slug) }))
      .filter((grupo) => grupo.rows.length)
  }

  get pendientes(): number {
    return this.progress.total - this.progress.answered
  }

  /** Lleva a la tarjeta exacta dentro de su manual. */
  abrir(row: DecisionRow): void {
    this.router.navigate(['/documentation', row.doc.slug], { fragment: row.decision.anchor })
  }

  trackByRow = (_: number, row: DecisionRow): string => `${row.doc.slug}:${row.decision.anchor}`
}
